<script setup lang="ts">
import Fitness from '@/views/slides/MinLivsstil/Slides/Fitness.vue';
import FitnessPrelude from '@/views/slides/MinLivsstil/Slides/FitnessPrelude.vue';
import {
  computed,
  inject,
  nextTick,
  onMounted,
  onUnmounted,
  provide,
  ref,
  watch,
  shallowRef,
  markRaw,
} from 'vue';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { IonicSlides } from '@ionic/vue';
import { Virtual } from 'swiper/modules';
import { useStore } from '@/stores/main';
import BIconButton from '@/components/BIconButton.vue';
import ArrowDown from '@/components/icons/ArrowDown.vue';
import Question from './Slides/Question.vue';
import PhysicalPrelude from './Slides/PhysicalPrelude.vue';
import PhysicalStrengthResults from '@/views/slides/MinLivsstil/Slides/PhysicalStrengthResults.vue';
import PhysicalEpilogue from './Slides/PhysicalEpilogue.vue';
import KasamPrelude from './Slides/KasamPrelude.vue';
import BuddySlide from './Slides/BuddySlide.vue';
import BuddyAlcoholSlide from './Slides/BuddyAlcoholSlide.vue';
import BuddyEnergySlide from './Slides/BuddyEnergySlide.vue';
import FoodBreakfast from './Slides/FoodBreakfast.vue';
import FoodLunch from './Slides/FoodLunch.vue';
import FoodDinner from './Slides/FoodDinner.vue';
import FoodSnacks from './Slides/FoodSnacks.vue';
import FoodSnacksCount from './Slides/FoodSnacksCount.vue';
import PhysicalResults from './Slides/PhysicalResults.vue';
import monsterUrl from '/assets/monster/arg.png';
import Cigarette from '@/components/icons/Cigarette.vue';
import Flower from '@/components/icons/Flower.vue';
import Food from '@/components/icons/Food.vue';
import Handball from '@/components/icons/Handball.vue';
import HandHeart from '@/components/icons/HandHeart.vue';
import People from '@/components/icons/People.vue';
import PersonCheck from '@/components/icons/PersonCheck.vue';
import { debounce } from 'lodash';
import BuddySlide4 from './Slides/BuddySlide4.vue';

// Type definitions
type SectionResult = {
  pageName: string;
  pageLabel: string;
  count: number;
  results: {
    healthy: number;
    risks: number;
    unknowns: number;
  };
  done: boolean;
  factors: {
    name: string;
    value: number;
  }[];
};

type SlideData = {
  label: string;
  type?: string;
  improve_name?: string;
  category_id?: number;
  questions?: Array<{
    name: string;
    type?: string;
  }>;
  value?: {
    text: string;
    color: string;
  };
  can_improve?: boolean;
};

// Component props
const props = defineProps<{
  sections?: any[];
  data: {
    pageName: string;
    groups: SlideData[];
  };
}>();

// Event emitters
const emit = defineEmits<{
  (e: 'index', value: number): void;
  (e: 'update-warning', state: boolean): void;
  (e: 'update'): void;
}>();

// Store and injected values
const store = useStore();
const colorScheme = inject('colorScheme', 'green');
const nextSectionSlide = inject('nextSlide') as (() => void) | undefined;
const previousSectionSlide = inject('previousSlide') as (() => void) | undefined;
const { shouldResetNavigation, setNavigationReset } = inject(
  'shouldResetNavigation',
) as any;
const updateWarningState = inject('updateWarningState', null) as ((val: boolean) => void) | null;

// Component state - use shallowRef for better performance
const state = shallowRef({
  isLoading: true,
  isNavigating: false,
  isAnimating: false,
  warningOpen: false,
  modalFadeIn: false,
  warningMessage: '',
  questionAnswered: false,
  canSkip: true,
  scrollButtonVisible: false,
  shouldScroll: false,
  selectedSlice: null as number | null,
  currentSlideIndex: 0,
  isValidating: false,
  loadingNextSlide: false,
  touchEnabled: false,
  uiReady: false,
});

// Navigation state
const slideDirections = shallowRef({
  next: false,
  previous: false,
});

// DOM refs
const slides = shallowRef();
const questionArea = shallowRef<HTMLElement | null>(null);
const navigationArea = shallowRef<HTMLElement | null>(null);

// Component cache
const componentCache = markRaw({
  physical: {
    prelude: PhysicalPrelude,
    'physical-strength-results': PhysicalStrengthResults,
    epilogue: PhysicalEpilogue,
    'fitness-result': FitnessPrelude,
    fitMethod: Fitness,
  },
  kasam: {
    prelude: KasamPrelude,
  },
  energy: {
    'buddy-energy': BuddyEnergySlide,
    'food-breakfast': FoodBreakfast,
    'food-lunch': FoodLunch,
    'food-dinner': FoodDinner,
    'food-snack': FoodSnacks,
    'food-snack-count': FoodSnacksCount,
  },
});

// Constants
const UI_CONSTANTS = {
  FILL_COLORS: [
    'fill-white',
    'fill-red-400',
    'fill-orange-400',
    'fill-amber-300',
    'fill-lime-300',
    'fill-green-400',
  ],
  BG_COLORS: [
    'bg-white',
    'bg-red-400',
    'bg-orange-400',
    'bg-amber-300',
    'bg-lime-300',
    'bg-green-400',
  ],
  ICON_COLORS: [
    'text-black/50',
    'text-black/60',
    'text-black/60',
    'text-black/50',
    'text-black/60',
    'text-black/60',
  ],
  ICON_COMPONENTS: [
    Handball,
    PersonCheck,
    HandHeart,
    Cigarette,
    Food,
    Flower,
    People,
  ],
  ICON_DIST: 23,
  ICON_HIGHLIGHT_DIST: 25,
};

// Memoized computed properties
const sectionResults = computed(() => {
  if (!props.sections) return [];

  const results: SectionResult[] = [];

  for (const section of props.sections) {
    if (section.pageName === 'kasam') {
      const factor = store.profile.factors?.[24];
      const value =
        factor?.value && factor.status !== 'unknown'
          ? Math.round((+factor.value / 7) * 5)
          : 0;

      results.push({
        pageName: section.pageName,
        pageLabel: section.pageLabel,
        count: 1,
        results: {
          healthy: value >= 3 ? 1 : 0,
          risks: value < 3 ? 1 : 0,
          unknowns: factor ? 0 : 1,
        },
        done: value > 0,
        factors: [{ name: 'KASAM', value }],
      });
    } else if (section.pageName === 'physical') {
      const groupsWithFactors = section.groups.filter(
        (group: any) =>
          group.category_id !== null &&
          (group.can_improve || group.category_id === 24) &&
          group.label !== 'physical-strength-results',
      );

      if (!groupsWithFactors.length) continue;

      const factors = groupsWithFactors.map((g: any) => {
        const factor = store.profile.factors?.[g.category_id];
        const value =
          factor && factor.status !== 'unknown'
            ? (factor.risk_value ?? factor.value ?? undefined)
            : undefined;

        return {
          name: g.label,
          value: value !== undefined ? +value : undefined,
        };
      });

      const { healthy, risks, unknowns } = groupsWithFactors.reduce(
        (acc: any, group: any) => {
          const factor = store.profile.factors?.[group.category_id];
          if (!factor) {
            acc.unknowns++;
          } else if (
            factor.status === 'profile.healthy' ||
            factor.status === 'healthy'
          ) {
            acc.healthy++;
          } else if (
            factor.status === 'profile.risk' ||
            factor.status === 'risk'
          ) {
            acc.risks++;
          } else {
            acc.unknowns++;
          }
          return acc;
        },
        { healthy: 0, risks: 0, unknowns: 0 },
      );

      results.push({
        pageName: section.pageName,
        pageLabel: section.pageLabel,
        count: healthy + risks + unknowns,
        results: { healthy, risks, unknowns },
        done: unknowns === 0,
        factors,
      });
    }
  }

  return results;
});

const totalAverageScore = computed(() => {
  const results = sectionResults.value;
  if (!results.length || !results.every(s => s.results.unknowns <= 0)) return 0;

  const sum = results.reduce((total, section) => {
    const sectionAvg =
      section.factors.reduce((acc, f) => acc + f.value, 0) /
      section.factors.length;
    return total + sectionAvg;
  }, 0);

  return sum / results.length;
});

const backgroundColor = computed(() => {
  const colorMap = {
    purple: 'bg-purple-background',
    yellow: 'bg-yellow-background',
    green: 'bg-green-background',
    blue: 'bg-blue-background',
  };
  return colorMap[colorScheme as keyof typeof colorMap];
});

// Chart visualization functions
function circleSegmentFill(section: any) {
  if (section.results.unknowns) return UI_CONSTANTS.FILL_COLORS[0];

  const sum = section.factors.reduce((a: number, f: any) => a + f.value, 0);
  const value = Math.round(sum / section.factors.length);
  return UI_CONSTANTS.FILL_COLORS[value];
}

const chartColors = computed(() =>
  sectionResults.value.map(section => circleSegmentFill(section)),
);

// Icon positioning functions
function icon(index: number) {
  return UI_CONSTANTS.ICON_COMPONENTS[index];
}

function iconX(index: number, total: number) {
  const angle = ((index + 0.5) / total) * Math.PI * 2;
  return 50 + Math.sin(angle) * UI_CONSTANTS.ICON_DIST + '%';
}

function iconY(index: number, total: number) {
  const angle = ((index + 0.5) / total) * Math.PI * 2;
  return 50 - Math.cos(angle) * UI_CONSTANTS.ICON_DIST + '%';
}

function iconColor(section: any) {
  if (section.results.unknowns) return UI_CONSTANTS.ICON_COLORS[0];
  return UI_CONSTANTS.ICON_COLORS[
    1 + Math.floor((section.results.healthy / section.count) * 4)
  ];
}

// State management functions
function updateState(updates: Record<string, any>) {
  requestAnimationFrame(() => {
    state.value = { ...state.value, ...updates };
  });
}

// Watch for warning state changes
watch(
  () => state.value.warningOpen,
  debounce(newValue => {
    emit('update-warning', newValue);
    if (updateWarningState) updateWarningState(newValue);
  }, 100),
);

// Swiper modules
const swiperModules = [IonicSlides, Virtual];

// Swiper initialization
function setSliderInstance(swiper: any) {
  slides.value = swiper;
  updateState({ currentSlideIndex: swiper.activeIndex || 0 });

  swiper.on('slideChange', slideChange);

  nextTick(() => {
    if (questionArea.value) questionArea.value.scrollTop = 0;
    setTimeout(() => updateState({ uiReady: true, isLoading: false }), 100);
  });
}

// Handle slide changes
let animationTimeout: number | null = null;
const slideChange = debounce((swiper: any) => {
  if (state.value.isAnimating) return;
  if (animationTimeout) clearTimeout(animationTimeout);

  updateState({ isAnimating: true, currentSlideIndex: swiper.activeIndex });

  store.savePoints = {
    ...store.savePoints,
    life: {
      ...store.savePoints?.life,
      question: swiper.activeIndex,
    },
  };

  emit('index', swiper.activeIndex);

  nextTick(() => {
    // Use instant scroll to prevent visible scrolling when changing slides
    if (questionArea.value) {
      questionArea.value.scrollTo({ top: 0, behavior: 'instant' });
    }
    const currentSlide = swiper.slides[swiper.activeIndex];
    if (currentSlide) {
      const scrollables = currentSlide.querySelectorAll('section, .scrollable');
      scrollables.forEach((section: Element) => {
        (section as HTMLElement).scrollTo({ top: 0, behavior: 'instant' });
      });
    }
  });

  animationTimeout = setTimeout(() => {
    updateState({ isAnimating: false });
    animationTimeout = null;
  }, swiper.params.speed);
}, 100);

// Navigation functions
function navigateSlide(direction: 'next' | 'prev', targetIndex?: number) {
  if (state.value.loadingNextSlide || !slides.value) return;

  const directions = {
    next: direction === 'next',
    previous: direction === 'prev',
  };
  updateState({ slideDirections: directions });

  const swiperInstance = slides.value;

  if (targetIndex !== undefined) {
    swiperInstance.slideTo(targetIndex);
  } else if (direction === 'next') {
    if (swiperInstance.isEnd) {
      updateState({ loadingNextSlide: true });
      nextSectionSlide?.();
      setTimeout(() => updateState({ loadingNextSlide: false }), 1000);
      return;
    }
    swiperInstance.slideNext();
  } else {
    if (swiperInstance.isBeginning) {
      updateState({ loadingNextSlide: true });
      previousSectionSlide?.();
      setTimeout(() => updateState({ loadingNextSlide: false }), 1000);
      return;
    }
    swiperInstance.slidePrev();
  }

  updateState({ questionAnswered: false });
  scrollToTop();
}

const nextSlide = () => {
  const group = props.data.groups[state.value.currentSlideIndex];
  const { canProceed, message } = validateCanProceed(group);

  if (!canProceed) {
    updateState({
      warningOpen: true,
      warningMessage: message,
    });

    setTimeout(() => updateState({ modalFadeIn: true }), 50);
    return;
  }

  navigateSlide('next');
};

const previousSlide = () => navigateSlide('prev');
const slideTo = (index: number) => navigateSlide('next', index);

// Validation function
const validateCanProceed = (() => {
  const validationRules = {
    'food-snack': (group: SlideData) => {
      const count = store.profile.values['energyIntakeNewSnackCount'] ?? 0;
      for (let i = 1; i <= count; i++) {
        const value = store.profile.values[`energyIntakeNewSnack${i}`];
        if (!value || value === '' || value === null) {
          return {
            canProceed: false,
            message: 'Du har inte svarat på frågan om dina mellanmål.',
          };
        }
      }
      return { canProceed: true, message: '' };
    },
    agility: (group: SlideData) => {
      const questions = [
        'shldrXL',
        'shldrXR',
        'pelIliL',
        'pelIliR',
        'pelHamL',
        'pelHamR',
      ];
      for (const name of questions) {
        const value = store.getProfileValue(name);
        if (value == null || value === '' || value === -1) {
          return {
            canProceed: false,
            message: 'Du har inte svarat på alla rörlighetsfrågor.',
          };
        }
      }
      return { canProceed: true, message: '' };
    },
    default: (group: SlideData) => {
      if (!group.questions?.length) return { canProceed: true, message: '' };

      const factor = group.category_id != null ? store.profile.factors?.[group.category_id!] : undefined;

      if (!factor) {
        const firstQuestion = group.questions[0];

        if (firstQuestion.type === 'twovalues') {
          const parts = firstQuestion.name.split(',');
          for (const part of parts) {
            const value = store.getProfileValue(part);
            if (value === null || value === undefined || value === '') {
              return {
                canProceed: false,
                message: 'Du har inte svarat på alla delar av frågan.',
              };
            }
          }
        } else if (group.improve_name === 'fitMethod') {
          if (!store.profile.factors?.[4]?.value) {
            return {
              canProceed: false,
              message: 'Du har inte svarat på frågan.',
            };
          } else if (store.profile.factors?.[4]?.improve === undefined) {
            return {
              canProceed: false,
              message: 'Du har inte svarat om du är nöjd eller vill förbättra.',
            };
          }
        } else {
          const value = store.getProfileValue(firstQuestion.name);
          if (value === null || value === undefined || value === '') {
            return {
              canProceed: false,
              message: 'Du har inte svarat på frågan.',
            };
          }
        }
      } else {
        if (
          factor.status === 'profile.unknown' ||
          factor.status === 'unknown'
        ) {
          return {
            canProceed: false,
            message: 'Du har inte svarat på frågan.',
          };
        }

        if (group.questions.length > 1) {
          const unansweredCount = group.questions.filter(q => {
            const value = store.getProfileValue(q.name);
            return value === null || value === undefined || value === '';
          }).length;

          if (unansweredCount > 0) {
            return {
              canProceed: false,
              message: `Du har inte svarat på alla frågor (${unansweredCount} kvar).`,
            };
          }
        }
      }

      if (
        group.can_improve &&
        factor &&
        (!factor.improve || factor.improve === '0')
      ) {
        return {
          canProceed: false,
          message: 'Du har inte svarat om du är nöjd eller vill förbättra.',
        };
      }

      return { canProceed: true, message: '' };
    },
  };

  return (group: SlideData) => {
    if (state.value.isValidating) return { canProceed: true, message: '' };

    updateState({ isValidating: true });

    const validator =
      group.type && group.type in validationRules
        ? validationRules[group.type as keyof typeof validationRules]
        : group.improve_name === 'agility'
          ? validationRules.agility
          : validationRules.default;

    const result = validator(group);

    updateState({
      isValidating: false,
      canSkip: !(group.type === 'food-snack'),
    });

    return result;
  };
})();

// Scroll management
const scrollToTop = () => {
  requestAnimationFrame(() => {
    if (questionArea.value) {
      questionArea.value.scrollTo({ top: 0, behavior: 'instant' });
    }
    const scrollingSection = document.querySelector(
      '.scrolling_down_section section',
    );
    if (scrollingSection instanceof HTMLElement) {
      scrollingSection.scrollTo({ top: 0, behavior: 'instant' });
    }
  });
};

// Dialog management
function ignoreWarning() {
  updateState({ modalFadeIn: false });

  setTimeout(() => {
    updateState({ warningOpen: false });
    navigateSlide('next');
  }, 300);
}

function closeWarningAndReset() {
  updateState({ modalFadeIn: false });

  setTimeout(() => {
    updateState({
      warningOpen: false,
      loadingNextSlide: false,
      isValidating: false,
      slideDirections: { next: false, previous: false },
    });
    nextTick(scrollToTop);
  }, 300);
}

// Handle navigation
function handleNextSlide() {
  if (state.value.warningOpen || state.value.loadingNextSlide) return;

  const group = props.data.groups[state.value.currentSlideIndex];
  const { canProceed, message } = validateCanProceed(group);

  if (!canProceed) {
    updateState({
      warningOpen: true,
      warningMessage: message,
    });

    setTimeout(() => updateState({ modalFadeIn: true }), 50);
    return;
  }

  nextSlide();
}

// Skip section
function skipSection() {
  if (state.value.loadingNextSlide) return;

  updateState({ loadingNextSlide: true });
  nextSectionSlide?.();
  scrollToTop();
}

function canSkipSection() {
  if (!slides.value) return false;

  const index = slides.value.activeIndex ?? 0;
  return (
    (props.data.pageName === 'kasam' || props.data.pageName === 'physical') &&
    index === 0
  );
}

// Watch for navigation reset
watch(shouldResetNavigation, async newValue => {
  if (!newValue || !slides.value) return;

  // Wait for next tick to ensure DOM is ready
  await nextTick();
  
  // Actually navigate to the first slide using the swiper instance
  if (slides.value) {
    // Reset immediately
    slides.value.slideTo(0, 0);
    slides.value.update();
    
    // Also reset after a delay to handle any timing issues
    setTimeout(() => {
      if (slides.value) {
        slides.value.slideTo(0, 0);
        slides.value.update();
      }
    }, 100);
  }
  
  // Reset the current slide index in state
  updateState({ currentSlideIndex: 0 });
  
  setNavigationReset();
}, { immediate: true });

// Scroll handling providers
provide('shouldScroll', {
  shouldScroll: computed(() => state.value.shouldScroll),
  scrollDown() {
    updateState({ shouldScroll: true });
  },
  resetShouldScroll() {
    updateState({ shouldScroll: false });
  },
});

watch(
  () => state.value.shouldScroll,
  value => {
    if (!value || !questionArea.value) return;

    requestAnimationFrame(() => {
      if (questionArea.value) {
        questionArea.value.scrollTop += 300;
      }
    });

    updateState({ shouldScroll: false });
  },
);

provide('questionData', props.data);
provide('swiperInstance', slides);

// Lifecycle
onMounted(() => {
  updateState({ isLoading: true });

  if (store.savePoints?.life?.question > 0) {
    nextTick(() => {
      if (slides.value) {
        slides.value.slideTo(store.savePoints.life.question, 0);
        setTimeout(scrollToTop, 100);
      }
    });
  } else {
    nextTick(scrollToTop);
  }
});

onUnmounted(() => {
  if (slides.value) {
    slides.value.off('slideChange', slideChange);
  }
  if (animationTimeout) {
    clearTimeout(animationTimeout);
    animationTimeout = null;
  }
});

// Memoized slide component getter
const getSlideComponent = computed(() => {
  // console.log(group)
  const cache = new Map();

  return (group: SlideData) => {
    if (cache.has(group.label)) return cache.get(group.label);
    const pageName = props.data.pageName;
    let component;
    if (pageName === 'physical') {
      if (group.improve_name === 'fitMethod') {
        component = componentCache.physical.fitMethod;
      } else {
        component =
          componentCache.physical[
            group.label as keyof typeof componentCache.physical
          ];
      }
    } else if (pageName === 'kasam') {
      component =
        componentCache.kasam[group.label as keyof typeof componentCache.kasam];
    } else if (pageName === 'energy') {
      const type = group.type || group.label;
      component =
        componentCache.energy[type as keyof typeof componentCache.energy];
    }

    console.log(group)
    if (!component) {
      if (group.label === 'buddy') component = BuddySlide;
      if(group.value?.text.includes("viktiga frågor")){
        component = BuddySlide4
      }
      if (group.category_id != null && +group.category_id === 97) component = PhysicalResults;
      component = component || Question;
    }

    cache.set(group.label, component);
    return component;
  };
});

function getComponentProps(group: SlideData) {
  if (group.label === 'buddy') {
    return { text: group.value?.text, color: group.value?.color };
  }
  return { data: group };
}

// Reset scroll position to top
function resetScroll() {
  if (questionArea.value) {
    questionArea.value.scrollTop = 0;
  }
  // Also reset the swiper wrapper height so content starts at top
  if (slides.value?.wrapperEl) {
    slides.value.wrapperEl.style.height = 'auto';
  }
}

// Expose methods
defineExpose({
  slideTo,
  nextSlide,
  previousSlide,
  resetScroll,
});
</script>

<template>
  <div
    :class="[
      backgroundColor,
      { 'opacity-0': state.isLoading, 'opacity-100': !state.isLoading },
    ]"
    class="lifestyle-view w-full overflow-hidden flex flex-col text-left transform translate-x-0 translate-y-0 transition-opacity duration-300"
  >
    <Teleport to="body">
      <!-- Warning Modal Overlay -->
      <div
        v-if="state.warningOpen"
        class="fixed inset-0 z-[99999] max-w-full w-full bg-black bg-opacity-40 transition-opacity duration-200 modal-overlay"
        :class="{
          'opacity-0': !state.modalFadeIn,
          'opacity-100': state.modalFadeIn,
        }"
        @click="closeWarningAndReset"
      ></div>

      <!-- Warning Dialog -->
      <dialog
        :open="state.warningOpen"
        class="bg-green-light w-[70%] sm:w-[70%] md:w-[50%] lg:w-[35%] xl:w-[30%] border-none rounded-xl shadow-2xl z-[999999] fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 p-5 sm:p-6 transition-opacity duration-200 m-0 custom-dialog"
        :class="{
          'opacity-0': !state.modalFadeIn,
          'opacity-100': state.modalFadeIn,
        }"
        @click.stop
      >
        <div class="flex flex-col items-center -mt-12 sm:-mt-14 pt-3">
          <img
            :src="monsterUrl"
            alt="Warning Monster"
            class="monster-img w-[50%] sm:w-[45%] md:w-[40%] lg:w-[35%] transform -translate-y-2"
            loading="lazy"
          />
          <p class="text-lg font-medium text-center mb-3 sm:mb-4">Stopp nu!</p>
          <p class="text-base leading-relaxed text-center mb-5 sm:mb-6">
            {{
              state.warningMessage ||
              (state.questionAnswered
                ? 'Du har inte svarat om du är nöjd eller vill förbättra.'
                : 'Du har inte svarat på frågan.')
            }}
          </p>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
            <b-button
              @click="closeWarningAndReset"
              look="secondary"
              class="hover:bg-opacity-90 transition-all"
            >
              Tillbaka
            </b-button>
            <b-button
              v-if="state.canSkip"
              @click="ignoreWarning"
              class="hover:bg-opacity-90 transition-all"
            >
              Gå vidare ändå
            </b-button>
          </div>
        </div>
      </dialog>
    </Teleport>

    <!-- Main Question Area -->
    <div
      class="relative z-4 scroll-smooth lg:pb-14 md:pb-5 overflow-y-auto overflow-x-hidden pb-40 max-h-[76svh] min-h-[76svh] scrolling_down_section scrollbar-hide"
      ref="questionArea"
    >
      <!-- Loading Indicator -->
      <div
        v-if="state.isLoading"
        class="absolute inset-0 flex items-center justify-center bg-opacity-50 z-10"
      >
        <div
          class="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"
        ></div>
      </div>

      <!-- Swiper Component -->
      <swiper
        :css-mode="false"
        :slides-per-view="1"
        :space-between="0"
        :virtual="true"
        :lazy="{ loadOnTransitionStart: true, loadPrevNext: false }"
        :watch-slides-progress="true"
        :preload-images="false"
        :speed="100"
        :threshold="30"
        :resistance-ratio="0.9"
        :initial-slide="0"
        :modules="swiperModules"
        @swiper="setSliderInstance"
        @activeIndexChange="slideChange"
        class="lifestyle-swiper overflow-hidden"
      >
        <swiper-slide
          v-for="(group, index) in data.groups"
          :key="`slide-${index}`"
          :virtualIndex="index"
        >
          <div
            class="slide-container"
            :class="{
              'slide-enter-active':
                slideDirections.next && state.currentSlideIndex === index,
              'slide-exit-active':
                slideDirections.previous &&
                state.currentSlideIndex === index + 1,
              'slide-previous-active':
                slideDirections.previous && state.currentSlideIndex === index,
              'slide-next-active':
                slideDirections.next && state.currentSlideIndex === index - 1,
            }"
          >
            <keep-alive>
              <component
                :is="getSlideComponent(group)"
                v-bind="getComponentProps(group)"
                @update="$emit('update')"
                @saved="() => {}"
              />
            </keep-alive>
          </div>
        </swiper-slide>
      </swiper>
    </div>

    <!-- Scroll Down Button -->
    <div
      v-if="state.scrollButtonVisible"
      class="scroll-button-container"
      @click="() => updateState({ shouldScroll: true })"
    >
      <b-icon-button class="scroll-button">
        <arrow-down />
      </b-icon-button>
    </div>

    <!-- Navigation Controls removed - handled by parent -->
    <div ref="navigationArea" class="hidden"></div>
  </div>
</template>

<style scoped>
/* Base Styles */
.lifestyle-view {
  --transition-speed: 300ms;
  --primary-color: #7b1fa2;
  font-family:
    system-ui,
    -apple-system,
    sans-serif;
}

/* Slide containers */
.lifestyle-swiper,
.slide-container {
  width: 100%;
}

.slide-container {
  transition: opacity 0.2s ease;
}

/* Content components */
:deep(.question-component),
:deep(.physical-component),
:deep(.kasam-component),
:deep(.food-component),
:deep(.buddy-component),
:deep(.question-content),
:deep(.slide-content) {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

:deep(.question-content),
:deep(.slide-content) {
  padding: 0 1rem;
}

@media (min-width: 768px) {
  :deep(.question-content),
  :deep(.slide-content) {
    padding: 0 1.5rem;
  }
}

@media (min-width: 1024px) {
  :deep(.question-content),
  :deep(.slide-content) {
    padding: 0 2rem;
  }
}

/* Swiper styles */
:deep(.swiper) {
  width: 100%;
  height: 100%;
}

:deep(.swiper-wrapper) {
  transition-timing-function: cubic-bezier(0.33, 1, 0.68, 1) !important;
}

:deep(.swiper-slide) {
  width: 100% !important;
  overflow-y: auto;
  overflow-x: hidden;
  height: auto;
}

/* Animation keyframes */
@keyframes slideInRight {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideOutLeft {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideOutRight {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Slide animations */
.slide-enter-active {
  animation: slideInRight 0.2s forwards;
}
.slide-exit-active {
  animation: slideOutLeft 0.2s forwards;
}
.slide-previous-active {
  animation: slideInLeft 0.2s forwards;
}
.slide-next-active {
  animation: slideOutRight 0.2s forwards;
}

/* Scroll button */
.scroll-button-container {
  position: fixed;
  bottom: 100px;
  right: 20px;
  z-index: 30;
  animation: fadeInUp 0.3s ease-out;
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1));
}

.scroll-button {
  background-color: #f9fcf2;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;
}

.scroll-button:hover {
  transform: translateY(-2px);
}

.scroll-button:active {
  transform: translateY(0);
}

/* Hidden navigation */
.hidden {
  display: none;
}

/* Scrollable area */
.scrolling_down_section {
  width: 100%;
  max-width: 100%;
}

/* Modal dialog overrides */
.custom-dialog {
  max-height: 90vh;
  overflow-y: auto;
}

/* Modal overlay */
.modal-overlay {
  background-color: rgba(0, 0, 0, 0.4);
}

/* Responsive optimizations */
@media (max-width: 375px) and (max-height: 667px) {
  .custom-dialog {
    width: 85% !important;
    padding: 1rem !important;
  }
  .monster-img {
    width: 50% !important;
  }
}

@media (orientation: landscape) and (max-height: 600px) {
  .scroll-button-container {
    bottom: 80px;
  }
  .monster-img {
    width: 35% !important;
  }
}

/* Performance optimizations */
:root {
  --swiper-transition-timing-function: cubic-bezier(0.33, 1, 0.68, 1);
}

/* Use CSS containment for better performance */
.slide-container {
  contain: layout style;
}

.lifestyle-view {
  contain: layout;
}

/* Optimize animations with will-change */
.slide-enter-active,
.slide-exit-active,
.slide-previous-active,
.slide-next-active {
  will-change: opacity;
}

/* Optimize scroll performance */
.scrolling_down_section {
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
}

/* Hide scrollbars but keep scrolling */
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

/* Reduce repaints during transitions */
.transition-opacity {
  will-change: opacity;
  backface-visibility: hidden;
}
</style>
